import type { ReactNode } from 'react';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import {
    KAZAKHSTAN_DIAL_CODE,
    formatSubscriberDigits,
    toSubscriberDigits
} from '../../core/phone/kazakhstan-phone';
import { Panel } from '../../ui/panel';
import { UserIcon } from '../../ui/portal-icons';
import { IinField } from './iin-field';
import { LanguagePreference } from './language-preference';
import { usePortalOutlet } from './use-portal-outlet';

/**
 * Who the company has on file, and the two things the customer may set about themselves: their IIN,
 * once, and the language they are written to in.
 *
 * The name, telephone and address are shown and not editable. Each of them is something a customer
 * signs in with or is reached by, and changing one is a conversation with an adviser rather than a
 * form.
 */
export function ProfileTab() {
    const { t } = useTranslation();
    const language = useLanguage();
    const { profile, recordIin } = usePortalOutlet();

    const name = profile === null ? '' : [profile.firstName, profile.lastName].filter(Boolean).join(' ');

    return (
        <div className="space-y-6">
            <Panel title={t('portal.personal_details')} icon={<UserIcon />}>
                <dl className="grid gap-4 sm:grid-cols-2">
                    <Detail label={t('portal.full_name')}>{name}</Detail>
                    <Detail label={t('portal.phone')}>
                        {profile?.phone
                            ? `${KAZAKHSTAN_DIAL_CODE} ${formatSubscriberDigits(toSubscriberDigits(profile.phone))}`
                            : ''}
                    </Detail>
                    <Detail label={t('portal.email')}>{profile?.email ?? ''}</Detail>
                </dl>

                <div className="mt-5 border-t border-slate-200/80 pt-5 dark:border-slate-800">
                    <IinField iin={profile?.iin ?? null} onRecorded={recordIin} />
                </div>
            </Panel>

            <LanguagePreference language={profile?.language ?? language} />
        </div>
    );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
    const { t } = useTranslation();

    return (
        <div className="min-w-0">
            <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</dt>
            {/* Wrapped rather than cut on a telephone: a customer checking their own address needs all of it. */}
            <dd className="mt-1 font-semibold wrap-anywhere sm:truncate text-slate-900 dark:text-slate-100">
                {children === '' ? (
                    <span className="font-normal text-slate-400 dark:text-slate-500">{t('portal.not_given')}</span>
                ) : (
                    children
                )}
            </dd>
        </div>
    );
}
